import React, { useState, useEffect, useCallback } from 'react';
import { Invoice } from '../types';
import { apiService } from '../services/api';
import { authService } from '../services/authService';
import EmailPreview from './EmailPreview';

interface EmailTemplate {
  id: string;
  name: string;
  display_name: string;
  language: string;
  type: string;
  file_path: string;
  description?: string;
  variables?: string[];
}


interface InvoiceDetailsSendEmailsTabProps {
  invoice: Invoice;
  onRefreshInvoice?: () => void;
}

const InvoiceDetailsSendEmailsTab: React.FC<InvoiceDetailsSendEmailsTabProps> = ({ 
  invoice, 
  onRefreshInvoice 
}) => {
    const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [recipientEmail, setRecipientEmail] = useState<string>('');
  const [customSubject, setCustomSubject] = useState<string>('');
  const [customMessage, setCustomMessage] = useState<string>('');
  const [language, setLanguage] = useState<'en' | 'de' | 'fr' | 'es' | 'es-ch' | 'pt' | 'pt-br'>('en');
  const [contractLanguage, setContractLanguage] = useState<string | null>(null);
  const [templateVariables, setTemplateVariables] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Test email state
  const [testEmail, setTestEmail] = useState<string>('');
  const [sendingTest, setSendingTest] = useState<boolean>(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testSuccess, setTestSuccess] = useState<string | null>(null);

  
  const fetchContractLanguage = useCallback(async () => {
    try {
      console.log(`[InvoiceDetailsSendEmailsTab] Fetching contract language for invoice ${invoice.invoice_id}`);
      
      // Get the invoice details to find the contract
      const invoiceResponse = await apiService.axiosInstance.get(`/api/v1/invoices/${invoice.invoice_id}`);
      const invoiceData = invoiceResponse.data;
      
      if (invoiceData.contract_id) {
        console.log(`[InvoiceDetailsSendEmailsTab] Found contract ID: ${invoiceData.contract_id}`);
        
        // Fetch contract details
        const contractResponse = await apiService.axiosInstance.get(`/api/v1/contracts/${invoiceData.contract_id}`);
        const contractData = contractResponse.data;
        
        if (contractData.language) {
          console.log(`[InvoiceDetailsSendEmailsTab] Contract language found: ${contractData.language}`);
          setContractLanguage(contractData.language);
          
          // Map contract language to supported email languages
          const languageMapping: Record<string, 'en' | 'de' | 'fr' | 'es' | 'es-ch' | 'pt' | 'pt-br'> = {
            'en': 'en',
            'de': 'de',
            'fr': 'fr',
            'es': 'es',
            'es-ch': 'es-ch',
            'pt': 'pt-br', // Map Portuguese to Brazilian Portuguese
            'pt-br': 'pt-br'
          };
          
          const mappedLanguage = languageMapping[contractData.language.toLowerCase()] || 'en';
          console.log(`[InvoiceDetailsSendEmailsTab] Setting email language to: ${mappedLanguage} (from contract: ${contractData.language})`);
          setLanguage(mappedLanguage);
        } else {
          console.log(`[InvoiceDetailsSendEmailsTab] No language found in contract, using default EN`);
        }
      } else {
        console.log(`[InvoiceDetailsSendEmailsTab] No contract ID found in invoice, using default EN`);
      }
    } catch (err: any) {
      console.warn(`[InvoiceDetailsSendEmailsTab] Failed to fetch contract language:`, err.response?.data || err.message);
      // Don't set error state for this, just use default language
    }
  }, [invoice.invoice_id]);

  const fetchEmailData = useCallback(async () => {
    try {
      console.log(`[InvoiceDetailsSendEmailsTab] Starting to fetch email data for invoice ${invoice.invoice_id}`);
      setLoading(true);
      setError(null);
      
      // Fetch email templates
      console.log(`[InvoiceDetailsSendEmailsTab] Fetching email templates...`);
      const templatesResponse = await apiService.axiosInstance.get(`/api/v1/emails/templates`).catch((err) => {
        console.warn(`[InvoiceDetailsSendEmailsTab] Failed to fetch email templates:`, err.response?.data || err.message);
        return { data: [] };
      });

      console.log(`[InvoiceDetailsSendEmailsTab] Email templates response:`, templatesResponse.data);

      // Filter templates to show only invoice notification templates
      const allTemplates = templatesResponse.data || [];
      const invoiceTemplates = allTemplates.filter((template: EmailTemplate) => 
        template.type === 'invoice_notification' || template.type === 'reminder'
      );
      console.log(`[InvoiceDetailsSendEmailsTab] Filtered invoice templates:`, invoiceTemplates);
      setEmailTemplates(invoiceTemplates);
      
      console.log(`[InvoiceDetailsSendEmailsTab] Successfully loaded email data`);
    } catch (err: any) {
      console.error(`[InvoiceDetailsSendEmailsTab] Error fetching email data:`, {
        error: err,
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        invoiceId: invoice.invoice_id
      });
      setError(err.response?.data?.detail || err.message || 'Failed to load email data');
    } finally {
      setLoading(false);
    }
  }, [invoice.invoice_id]);

  const fetchTemplateDetails = async (templateId: string) => {
    try {
      console.log(`[InvoiceDetailsSendEmailsTab] Fetching template details for template ID: ${templateId}`);
      const response = await apiService.axiosInstance.get(`/api/v1/emails/templates/${templateId}`);
      console.log(`[InvoiceDetailsSendEmailsTab] Template details response:`, response.data);
      
      const templateData = response.data;
      if (templateData && templateData.variables) {
        console.log(`[InvoiceDetailsSendEmailsTab] Template variables found:`, templateData.variables);
        setTemplateVariables(templateData.variables);
      } else {
        console.log(`[InvoiceDetailsSendEmailsTab] No template variables found for template ${templateId}`);
        setTemplateVariables([]);
      }
    } catch (err: any) {
      console.error(`[InvoiceDetailsSendEmailsTab] Error fetching template details for ${templateId}:`, {
        error: err,
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setTemplateVariables([]);
    }
  };

  const fetchEmailPreview = useCallback(async () => {
    try {
      setPreviewLoading(true);
      setPreviewError(null);

      const response = await apiService.axiosInstance.post(
        `/api/v1/invoices/${invoice.invoice_id}/emails/preview`,
        {
          language: language,
          custom_message: customMessage.trim() || undefined,
          recipient_email: recipientEmail.trim() || invoice.customer_email_address || 'preview@example.com',
          template_id: selectedTemplate || undefined,
          custom_subject: customSubject.trim() || undefined
        }
      );

      setEmailPreviewData(response.data);
    } catch (err: any) {
      console.error('Error fetching email preview:', err);
      setPreviewError(err.response?.data?.detail || err.message || 'Failed to load email preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [invoice.invoice_id, language, customMessage, customSubject, recipientEmail, selectedTemplate, invoice.customer_email_address]);

  // Init effect after callbacks are defined
  useEffect(() => {
    console.log(`[InvoiceDetailsSendEmailsTab] Component mounted/updated for invoice ${invoice.invoice_id}`);
    console.log(`[InvoiceDetailsSendEmailsTab] Invoice data:`, {
      invoiceId: invoice.invoice_id,
      invoiceNumber: invoice.invoice_number,
      invoiceStatus: invoice.invoice_status,
      customerEmail: invoice.customer_email_address
    });

    fetchEmailData();
    fetchContractLanguage();

    // Set default recipient email from invoice
    if (invoice.customer_email_address) {
      console.log(`[InvoiceDetailsSendEmailsTab] Setting default recipient email: ${invoice.customer_email_address}`);
      setRecipientEmail(invoice.customer_email_address);
    } else {
      console.log(`[InvoiceDetailsSendEmailsTab] No customer email found in invoice data`);
    }
  }, [invoice.invoice_id, invoice.customer_email_address, invoice.invoice_number, invoice.invoice_status, fetchEmailData, fetchContractLanguage]);

  // Fetch email preview when form data changes
  useEffect(() => {
    if (recipientEmail.trim()) {
      fetchEmailPreview();
    }
  }, [fetchEmailPreview, recipientEmail]);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    if (templateId) {
      fetchTemplateDetails(templateId);
    } else {
      setTemplateVariables([]);
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log(`[InvoiceDetailsSendEmailsTab] Starting email send process for invoice ${invoice.invoice_id}`);
    
    // Validation checks with logging
    if (!recipientEmail.trim()) {
      const errorMsg = 'Recipient email is required';
      console.warn(`[InvoiceDetailsSendEmailsTab] Validation failed: ${errorMsg}`);
      setError(errorMsg);
      return;
    }

    if (invoice.invoice_status === 'draft') {
      const errorMsg = 'Cannot send emails for draft invoices. Please post the invoice first.';
      console.warn(`[InvoiceDetailsSendEmailsTab] Validation failed: ${errorMsg}`, {
        invoiceId: invoice.invoice_id,
        invoiceStatus: invoice.invoice_status
      });
      setError(errorMsg);
      return;
    }

    try {
      setSending(true);
      setError(null);
      console.log(`[InvoiceDetailsSendEmailsTab] Email validation passed, proceeding with send`);

      const currentUser = authService.getUser();
      if (!currentUser) {
        const errorMsg = 'You must be logged in to send emails';
        console.error(`[InvoiceDetailsSendEmailsTab] Authentication failed: ${errorMsg}`);
        throw new Error(errorMsg);
      }
      console.log(`[InvoiceDetailsSendEmailsTab] User authenticated:`, { userId: currentUser.id, email: currentUser.email });

      const emailData = {
        recipient_email: recipientEmail.trim(),
        language: language,
        custom_subject: customSubject.trim() || undefined,
        custom_message: customMessage.trim() || undefined,
        template_id: selectedTemplate || undefined
      };

      console.log(`[InvoiceDetailsSendEmailsTab] Sending email with data:`, {
        invoiceId: invoice.invoice_id,
        recipientEmail: emailData.recipient_email,
        language: emailData.language,
        hasCustomSubject: !!emailData.custom_subject,
        hasCustomMessage: !!emailData.custom_message,
        templateId: emailData.template_id,
        invoiceNumber: invoice.invoice_number
      });

      console.log(`[InvoiceDetailsSendEmailsTab] Making API call to send email...`);
      const response = await apiService.axiosInstance.post(`/api/v1/invoices/${invoice.invoice_id}/emails/send`, emailData);
      console.log(`[InvoiceDetailsSendEmailsTab] Email send API response:`, response.data);

      console.log(`[InvoiceDetailsSendEmailsTab] Email sent successfully, refreshing email history...`);
      // Refresh email history
      await fetchEmailData();
      
      // Update invoice sending status
      console.log(`[InvoiceDetailsSendEmailsTab] Refreshing invoice data...`);
      onRefreshInvoice?.();

      // Reset form
      console.log(`[InvoiceDetailsSendEmailsTab] Resetting form fields...`);
      setCustomSubject('');
      setCustomMessage('');
      setSelectedTemplate('');

      console.log(`[InvoiceDetailsSendEmailsTab] Email send process completed successfully`);
      alert('Email sent successfully!');
    } catch (err: any) {
      console.error(`[InvoiceDetailsSendEmailsTab] Error sending email:`, {
        error: err,
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText,
        invoiceId: invoice.invoice_id,
        recipientEmail: recipientEmail,
        requestData: {
          recipient_email: recipientEmail.trim(),
          language: language,
          custom_subject: customSubject.trim() || undefined,
          custom_message: customMessage.trim() || undefined,
          template_id: selectedTemplate || undefined
        }
      });
      
      // Enhanced error message extraction
      let errorMessage = 'Failed to send email';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Add status code to error message for better debugging
      if (err.response?.status) {
        errorMessage = `${errorMessage} (HTTP ${err.response.status})`;
      }
      
      console.error(`[InvoiceDetailsSendEmailsTab] Setting error message: ${errorMessage}`);
      setError(errorMessage);
    } finally {
      console.log(`[InvoiceDetailsSendEmailsTab] Email send process finished, setting sending to false`);
      setSending(false);
    }
  };

  
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <p>Loading email data...</p>
      </div>
    );
  }

  return (
    <div className="send-emails-tab">
      {/* Send Email Form */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3>Send Invoice Email</h3>
          {contractLanguage && (
            <small className="text-muted">
              Contract language: <strong>{contractLanguage.toUpperCase()}</strong> 
              {contractLanguage !== language && ` (mapped to ${language.toUpperCase()} for email)`}
            </small>
          )}
        </div>
        <div className="card-body">
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
              <div className="error-header">
                <strong>⚠️ Email Send Failed</strong>
              </div>
              <div className="error-message">
                {error}
              </div>
              <div className="error-help">
                <small>
                  Check the browser console (F12) for detailed error information. 
                  If the problem persists, please contact support.
                </small>
              </div>
            </div>
          )}

          {invoice.invoice_status === 'draft' && (
            <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
              This invoice is still in draft status. Please post the invoice before sending emails.
            </div>
          )}

          <form onSubmit={handleSendEmail}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="recipientEmail">Recipient Email *</label>
                <input
                  type="email"
                  id="recipientEmail"
                  className="form-control"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  required
                  disabled={sending || invoice.invoice_status === 'draft'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="emailTemplate">Email Template</label>
                <select
                  id="emailTemplate"
                  className="form-control"
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  disabled={sending || invoice.invoice_status === 'draft'}
                >
                  <option value="">Custom Email</option>
                  {emailTemplates
                    .filter((template) => 
                      template.language === language && 
                      (template.type === 'invoice_notification' || template.type === 'reminder')
                    )
                    .map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.display_name}
                      </option>
                    ))}
                </select>
                {selectedTemplate && templateVariables.length > 0 && (
                  <div className="template-info">
                    <small className="text-muted">
                      <strong>Available variables:</strong> {templateVariables.join(', ')}
                    </small>
                    <br />
                    <small className="text-muted">
                      These variables will be automatically filled with invoice data.
                    </small>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="customSubject">Subject</label>
                <input
                  type="text"
                  id="customSubject"
                  className="form-control"
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  placeholder={`Invoice ${invoice.invoice_number}`}
                  disabled={sending || invoice.invoice_status === 'draft'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="language">
                  Language 
                  {contractLanguage && (
                    <small className="text-muted"> (auto-detected from contract: {contractLanguage})</small>
                  )}
                </label>
                <select
                  id="language"
                  className="form-control"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as 'en' | 'de' | 'fr' | 'es' | 'es-ch' | 'pt' | 'pt-br')}
                  disabled={sending || invoice.invoice_status === 'draft'}
                >
                  <option value="en">English</option>
                  <option value="de">German</option>
                  <option value="fr">French</option>
                  <option value="es">Spanish</option>
                  <option value="es-ch">Spanish (Switzerland)</option>
                  <option value="pt">Portuguese</option>
                  <option value="pt-br">Portuguese (Brazil)</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="customMessage">Message</label>
              <textarea
                id="customMessage"
                className="form-control"
                rows={4}
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder={`Please find attached invoice ${invoice.invoice_number}.`}
                disabled={sending || invoice.invoice_status === 'draft'}
              />
            </div>

            <div className="form-group">
              <div className="info-box">
                <small className="text-muted">
                  <strong>Note:</strong> Invoice PDF will be automatically attached to the email. 
                  For Brazilian customers with tax ID, a Boleto payment slip will also be included if applicable.
                  {contractLanguage && (
                    <>
                      <br />
                      <strong>Language:</strong> Email language is automatically set based on the contract language ({contractLanguage}).
                    </>
                  )}
                </small>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPreview(true)}
                disabled={!recipientEmail.trim()}
                style={{ marginRight: '10px' }}
              >
                👁️ Preview Email
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sending || invoice.invoice_status === 'draft'}
              >
                {sending ? (
                  <>
                    <span className="spinner-small"></span>
                    Sending Email...
                  </>
                ) : (
                  'Send Email'
                )}
              </button>
              {sending && (
                <div className="sending-status">
                  <small className="text-muted">
                    📧 Preparing and sending email to {recipientEmail}...
                  </small>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Email Preview Section */}
      <div className="card">
        <div className="card-header">
          <h3>Email Preview</h3>
          <small className="text-muted">This is how the email will look when sent</small>
        </div>
        <div className="card-body">
          {/* Test email UI */}
          <div className="test-email-box" style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e9ecef', borderRadius: 4 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label htmlFor="testEmail" style={{ fontWeight: 500, margin: 0 }}>Send Test Email</label>
              <input
                type="email"
                id="testEmail"
                className="form-control"
                placeholder="name@ecomply.io"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                style={{ maxWidth: 300 }}
                disabled={sendingTest}
              />
              <button
                type="button"
                className="btn btn-secondary"
                disabled={sendingTest || !/^[^@\s]+@ecomply\.io$/i.test(testEmail)}
                onClick={async () => {
                  setTestError(null);
                  setTestSuccess(null);
                  if (!/^[^@\s]+@ecomply\.io$/i.test(testEmail)) {
                    setTestError('Only @ecomply.io addresses are allowed for test emails');
                    return;
                  }
                  try {
                    setSendingTest(true);
                    const payload: any = {
                      recipient_email: testEmail.trim(),
                      language: language,
                      custom_subject: customSubject.trim() || undefined,
                      custom_message: customMessage.trim() || undefined,
                      template_id: selectedTemplate || undefined,
                      send_test: true
                    };
                    await apiService.axiosInstance.post(`/api/v1/invoices/${invoice.invoice_id}/emails/send`, payload);
                    setTestSuccess('Test email sent successfully');
                  } catch (err: any) {
                    const msg = err.response?.data?.detail || err.message || 'Failed to send test email';
                    setTestError(msg);
                  } finally {
                    setSendingTest(false);
                  }
                }}
              >
                {sendingTest ? 'Sending…' : 'Send Test Email'}
              </button>
              <small className="text-muted">Test emails are restricted to @ecomply.io addresses and do not use contract recipients.</small>
            </div>
            {(testError || testSuccess) && (
              <div style={{ marginTop: 8 }}>
                {testError && <div className="alert alert-danger" style={{ marginBottom: 8 }}>{testError}</div>}
                {testSuccess && <div className="alert alert-success" style={{ marginBottom: 8 }}>{testSuccess}</div>}
              </div>
            )}
          </div>
          {previewLoading && (
            <div className="preview-loading">
              <div className="spinner-small"></div>
              <span>Loading email preview...</span>
            </div>
          )}

          {previewError && (
            <div className="alert alert-warning">
              <strong>⚠️ Preview Error</strong>
              <p>{previewError}</p>
              <button onClick={fetchEmailPreview} className="btn btn-sm btn-primary">
                Retry Preview
              </button>
            </div>
          )}

          {!previewLoading && !previewError && emailPreviewData && (
            <div className="email-preview-container">
              {/* Email Meta Information */}
              <div className="email-meta">
                <div className="meta-row">
                  <span className="meta-label">Subject:</span>
                  <span className="meta-value">{emailPreviewData.subject}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">To:</span>
                  <span className="meta-value">{(emailPreviewData.to_recipients && emailPreviewData.to_recipients.length > 0) ? emailPreviewData.to_recipients.join(', ') : emailPreviewData.recipient_email}</span>
                </div>
                {emailPreviewData.cc_recipients && emailPreviewData.cc_recipients.length > 0 && (
                  <div className="meta-row">
                    <span className="meta-label">CC:</span>
                    <span className="meta-value">{emailPreviewData.cc_recipients.join(', ')}</span>
                  </div>
                )}
                {emailPreviewData.bcc_recipients && emailPreviewData.bcc_recipients.length > 0 && (
                  <div className="meta-row">
                    <span className="meta-label">BCC:</span>
                    <span className="meta-value">{emailPreviewData.bcc_recipients.join(', ')}</span>
                  </div>
                )}
                <div className="meta-row">
                  <span className="meta-label">Language:</span>
                  <span className="meta-value">
                    {emailPreviewData.language?.toUpperCase()}
                    {contractLanguage && contractLanguage !== emailPreviewData.language && (
                      <small className="text-muted"> (from contract: {contractLanguage.toUpperCase()})</small>
                    )}
                  </span>
                </div>
              </div>

              {/* Rendered HTML Content */}
              <div className="email-html-preview">
                <div className="preview-header">
                  <h4>Email Body (HTML)</h4>
                  <button 
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      if (printWindow && emailPreviewData) {
                        printWindow.document.write(`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>Email Preview - ${emailPreviewData.subject}</title>
                            <style>
                              body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
                              .print-header { 
                                border-bottom: 2px solid #ccc; 
                                padding-bottom: 10px; 
                                margin-bottom: 20px; 
                              }
                              .print-header h1 { margin: 0; color: #333; }
                              .print-header p { margin: 5px 0; color: #666; }
                            </style>
                          </head>
                          <body>
                            <div class="print-header">
                              <h1>Email Preview</h1>
                              <p><strong>Subject:</strong> ${emailPreviewData.subject}</p>
                              <p><strong>To:</strong> ${(emailPreviewData.to_recipients && emailPreviewData.to_recipients.length > 0) ? emailPreviewData.to_recipients.join(', ') : emailPreviewData.recipient_email}</p>
                              ${emailPreviewData.cc_recipients && emailPreviewData.cc_recipients.length ? `<p><strong>CC:</strong> ${emailPreviewData.cc_recipients.join(', ')}</p>` : ''}
                              ${emailPreviewData.bcc_recipients && emailPreviewData.bcc_recipients.length ? `<p><strong>BCC:</strong> ${emailPreviewData.bcc_recipients.join(', ')}</p>` : ''}
                              <p><strong>Language:</strong> ${emailPreviewData.language?.toUpperCase()}</p>
                            </div>
                            ${emailPreviewData.html_body}
                          </body>
                          </html>
                        `);
                        printWindow.document.close();
                        printWindow.print();
                      }
                    }}
                    className="btn btn-sm btn-secondary"
                  >
                    🖨️ Print Preview
                  </button>
                </div>
                <div className="html-content-frame">
                  <iframe
                    srcDoc={emailPreviewData.html_body}
                    title="Email HTML Preview"
                    className="email-iframe"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            </div>
          )}

          {!previewLoading && !previewError && !emailPreviewData && recipientEmail.trim() && (
            <div className="empty-preview">
              <p className="text-muted">
                Email preview will appear here once the form is filled out.
              </p>
            </div>
          )}

          {!recipientEmail.trim() && (
            <div className="empty-preview">
              <p className="text-muted">
                Please enter a recipient email address to see the email preview.
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .send-emails-tab .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 15px;
          margin-bottom: 15px;
        }

        .send-emails-tab .form-group {
          margin-bottom: 15px;
        }

        .send-emails-tab .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
          color: #333;
        }

        .send-emails-tab .form-control {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .send-emails-tab .form-control:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        .send-emails-tab .form-control:disabled {
          background-color: #f8f9fa;
          color: #6c757d;
        }

        .send-emails-tab .checkbox-group {
          margin-bottom: 10px;
        }

        .send-emails-tab .checkbox-label {
          display: flex;
          align-items: center;
          font-weight: normal;
          margin-bottom: 0;
        }

        .send-emails-tab .checkbox-label input[type="checkbox"] {
          margin-right: 8px;
        }

        .send-emails-tab .attachment-options {
          margin-left: 20px;
          margin-top: 10px;
        }

        .send-emails-tab .radio-label {
          display: flex;
          align-items: center;
          font-weight: normal;
          margin-bottom: 5px;
        }

        .send-emails-tab .radio-label input[type="radio"] {
          margin-right: 8px;
        }

        .send-emails-tab .form-actions {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }

        .send-emails-tab .spinner-small {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid #ffffff;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }

        .send-emails-tab .sending-status {
          margin-top: 10px;
          padding: 8px;
          background-color: #e7f3ff;
          border-radius: 4px;
          border-left: 3px solid #007bff;
        }

        .send-emails-tab .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #6c757d;
        }

        .send-emails-tab .loading {
          text-align: center;
          padding: 40px 20px;
        }

        .send-emails-tab .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .send-emails-tab .alert {
          padding: 12px 15px;
          border-radius: 4px;
          margin-bottom: 15px;
        }

        .send-emails-tab .alert-danger {
          background-color: #f8d7da;
          border-color: #f5c6cb;
          color: #721c24;
        }
        .send-emails-tab .alert-success {
          background-color: #d4edda;
          border-color: #c3e6cb;
          color: #155724;
        }

        .send-emails-tab .error-header {
          margin-bottom: 8px;
          font-size: 16px;
        }

        .send-emails-tab .error-message {
          margin-bottom: 8px;
          font-family: monospace;
          background-color: rgba(0, 0, 0, 0.05);
          padding: 8px;
          border-radius: 4px;
          word-break: break-word;
        }

        .send-emails-tab .error-help {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid rgba(0, 0, 0, 0.1);
        }

        .send-emails-tab .alert-warning {
          background-color: #fff3cd;
          border-color: #ffeaa7;
          color: #856404;
        }

        .send-emails-tab .badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .send-emails-tab .badge-success {
          background-color: #28a745;
          color: white;
        }

        .send-emails-tab .badge-danger {
          background-color: #dc3545;
          color: white;
        }

        .send-emails-tab .badge-warning {
          background-color: #ffc107;
          color: #212529;
        }

        .send-emails-tab .badge-secondary {
          background-color: #6c757d;
          color: white;
        }

        .send-emails-tab .error-message {
          color: #dc3545;
          font-size: 12px;
          font-style: italic;
        }

        .send-emails-tab .template-info {
          margin-top: 8px;
          padding: 8px;
          background-color: #f8f9fa;
          border-radius: 4px;
          border-left: 3px solid #007bff;
        }

        .send-emails-tab .template-info small {
          display: block;
          margin-bottom: 4px;
        }

        .send-emails-tab .info-box {
          padding: 10px;
          background-color: #e7f3ff;
          border-left: 4px solid #007bff;
          border-radius: 4px;
          margin-top: 10px;
        }

        .send-emails-tab .text-muted {
          color: #6c757d;
        }

        /* Email Preview Styles */
        .send-emails-tab .preview-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          gap: 10px;
          color: #6c757d;
        }

        .send-emails-tab .email-preview-container {
          margin-top: 15px;
        }

        .send-emails-tab .email-meta {
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          padding: 15px;
          margin-bottom: 20px;
        }

        .send-emails-tab .meta-row {
          display: flex;
          margin-bottom: 8px;
        }

        .send-emails-tab .meta-row:last-child {
          margin-bottom: 0;
        }

        .send-emails-tab .meta-label {
          font-weight: 600;
          color: #495057;
          min-width: 80px;
          margin-right: 10px;
        }

        .send-emails-tab .meta-value {
          color: #212529;
          flex: 1;
          word-break: break-word;
        }

        .send-emails-tab .email-html-preview {
          border: 1px solid #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        }

        .send-emails-tab .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 15px;
          background-color: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
        }

        .send-emails-tab .preview-header h4 {
          margin: 0;
          font-size: 16px;
          color: #495057;
        }

        .send-emails-tab .html-content-frame {
          height: 500px;
          overflow: hidden;
        }

        .send-emails-tab .email-iframe {
          width: 100%;
          height: 100%;
          border: none;
          background: white;
        }

        .send-emails-tab .empty-preview {
          text-align: center;
          padding: 40px 20px;
          color: #6c757d;
          background-color: #f8f9fa;
          border-radius: 4px;
          border: 1px dashed #dee2e6;
        }

        .send-emails-tab .btn-sm {
          padding: 4px 8px;
          font-size: 12px;
          border-radius: 3px;
        }

        .send-emails-tab .btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
          color: white;
        }

        .send-emails-tab .btn-secondary:hover {
          background-color: #5a6268;
          border-color: #545b62;
        }

        .send-emails-tab .btn-primary {
          background-color: #007bff;
          border-color: #007bff;
          color: white;
        }

        .send-emails-tab .btn-primary:hover {
          background-color: #0056b3;
          border-color: #004085;
        }

        @media (max-width: 768px) {
          .send-emails-tab .preview-header {
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
          }

          .send-emails-tab .html-content-frame {
            height: 400px;
          }

          .send-emails-tab .meta-row {
            flex-direction: column;
            gap: 2px;
          }

          .send-emails-tab .meta-label {
            min-width: auto;
            margin-right: 0;
          }
        }
      `}</style>

      {/* Email Preview Modal */}
      {showPreview && (
        <EmailPreview
          invoice={invoice}
          language={language}
          customMessage={customMessage.trim() || undefined}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default InvoiceDetailsSendEmailsTab;